import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CalendarDays,
  FileText,
  Receipt,
  CreditCard,
  Gift,
  Globe,
  Trophy,
  TrendingUp,
  X,
  CheckCheck,
  Info,
  ClipboardList,
  Package,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/services/api';
import { cn, formatDateTime } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NotificationType =
  | 'leave_approved'
  | 'leave_rejected'
  | 'leave_pending'
  | 'policy_pending'
  | 'expense_approved'
  | 'expense_rejected'
  | 'payslip_available'
  | 'birthday'
  | 'recognition'
  | 'onboarding_feedback'
  | 'asset_request'
  | 'visa_renewal'
  | 'pip_update'
  | 'general';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  is_read: boolean;
  action_url: string | null;
  created_at: string;
}

interface NotificationsResponse {
  items: Notification[];
  unread_count: number;
}

// ---------------------------------------------------------------------------
// Query key
// ---------------------------------------------------------------------------

const notifKeys = {
  all: ['notifications'] as const,
  list: () => [...notifKeys.all, 'list'] as const,
};

// ---------------------------------------------------------------------------
// Icon per type
// ---------------------------------------------------------------------------

function NotifIcon({ type }: { type: NotificationType }) {
  const base = 'h-4 w-4 shrink-0';
  switch (type) {
    case 'leave_approved':
    case 'leave_rejected':
    case 'leave_pending':
      return <CalendarDays className={cn(base, 'text-emerald-500')} />;
    case 'policy_pending':
      return <FileText className={cn(base, 'text-amber-500')} />;
    case 'expense_approved':
    case 'expense_rejected':
      return <Receipt className={cn(base, 'text-sky-500')} />;
    case 'payslip_available':
      return <CreditCard className={cn(base, 'text-violet-500')} />;
    case 'birthday':
      return <Gift className={cn(base, 'text-pink-500')} />;
    case 'recognition':
      return <Trophy className={cn(base, 'text-amber-500')} />;
    case 'onboarding_feedback':
      return <ClipboardList className={cn(base, 'text-indigo-500')} />;
    case 'asset_request':
      return <Package className={cn(base, 'text-orange-500')} />;
    case 'visa_renewal':
      return <Globe className={cn(base, 'text-rose-500')} />;
    case 'pip_update':
      return <TrendingUp className={cn(base, 'text-purple-500')} />;
    default:
      return <Info className={cn(base, 'text-zinc-500')} />;
  }
}

// ---------------------------------------------------------------------------
// Single notification row
// ---------------------------------------------------------------------------

function NotifItem({
  notif,
  onClose,
}: {
  notif: Notification;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const markRead = useMutation({
    mutationFn: (id: string) =>
      post<void>(`/notifications/${id}/read`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notifKeys.list() });
    },
  });

  const handleClick = () => {
    if (!notif.is_read) {
      markRead.mutate(notif.id);
    }
    if (notif.action_url) {
      navigate(notif.action_url);
    }
    onClose();
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-zinc-50',
        !notif.is_read && 'bg-zinc-50'
      )}
    >
      <div className="mt-0.5">
        <NotifIcon type={notif.type} />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm leading-snug',
            notif.is_read ? 'text-zinc-500' : 'text-zinc-900 font-medium'
          )}
        >
          {notif.title}
        </p>
        {notif.body && (
          <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{notif.body}</p>
        )}
        <p className="text-xs text-zinc-400 mt-1">{formatDateTime(notif.created_at)}</p>
      </div>
      {!notif.is_read && (
        <div className="mt-1.5 h-2 w-2 rounded-full bg-sky-500 shrink-0" />
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main NotificationCenter
// ---------------------------------------------------------------------------

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: notifKeys.list(),
    queryFn: () => get<NotificationsResponse>('/notifications'),
    refetchInterval: 30_000,
    staleTime: 20_000,
    retry: false,
  });

  const markAll = useMutation({
    mutationFn: () => post<void>('/notifications/read-all'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notifKeys.list() });
    },
  });

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    function handleClick(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const unreadCount = data?.unread_count ?? 0;
  const notifications = data?.items ?? [];

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        className="relative text-zinc-500 hover:text-zinc-700 transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 rounded-xl border border-zinc-200 bg-white shadow-lg z-50 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-zinc-900">Notifications</h3>
              {unreadCount > 0 && (
                <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-600">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={() => markAll.mutate()}
                  disabled={markAll.isPending}
                  className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-700 transition-colors"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="text-zinc-400 hover:text-zinc-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto divide-y divide-zinc-100">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-zinc-400">
                <Bell className="h-8 w-8 mb-2 opacity-30" />
                <p className="text-sm">No notifications</p>
                <p className="text-xs mt-0.5 text-zinc-400">
                  You're all caught up
                </p>
              </div>
            ) : (
              notifications.map((notif) => (
                <NotifItem key={notif.id} notif={notif} onClose={() => setOpen(false)} />
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-zinc-200 px-4 py-3 text-center">
              <button
                onClick={() => {
                  setOpen(false);
                  navigate('/notifications');
                }}
                className="text-xs text-zinc-500 hover:text-zinc-700 transition-colors"
              >
                View all activity
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
